/**
 * @file useSearch
 *
 * @fileoverview Encapuslates logic about search including URL manipulation. It takes a search page and
 * optional parameters for the number of clean facets in the url (without Get param) and also for which order
 * facets should show up in the URL.
 */

import {
  redirectSearchWithFilter,
  redirectWithoutFulltextAndLocation,
  redirectWithFulltextAndOrLocation,
} from '../utils/searchRedirect';
import { getFilterSelectedItems } from '../utils/searchHelpers';
import { Facet } from '~/jobiqo/models/search';
import { GeoInput } from '~/jobiqo/types/types';
import { flattenForChildren } from '~/jobiqo/services/taxonomyHelpers';

interface UseSearchType {
  getFilterSelectedItems: (querystring: any, facet: Facet) => string[];
  toggleFilter: (
    filter: string,
    selectedItems: string[],
    alias?: string,
  ) => Promise<boolean>;
  redirectWithoutFulltextAndLocation: () => void;
  redirectWithFulltextAndOrLocation: (
    fulltext?: string,
    location?: GeoInput,
  ) => void;
  getFilterSelectedFacets: (querystring: any, facets: Facet[]) => Facet[];
}

/**
 * Handles toggling filters on and off.
 *
 * Will do a redirect with the facet that was selected.
 */
const toggleFilter = (
  searchUrl: string,
  filter: string,
  cleanFacetsNumber: number,
  cleanFacetsOrder: string[],
  selectedItems?: string[],
  alias?: string,
) =>
  redirectSearchWithFilter(
    searchUrl,
    filter,
    cleanFacetsNumber,
    cleanFacetsOrder,
    selectedItems,
    alias,
  );

/**
 * Extracts selected facets from facet array.
 *
 * @param {string} querystring
 *  Current query string.
 * @param {Facet[]} Facets
 *  Facets to be filtered.
 * @returns {Facet[]}
 *  Selected facets.
 */
const extractSelectedFacetValues = (
  querystring: string,
  facets: Facet[],
): Facet[] => {
  const facetIdsArray: Array<Array<String>> = facets?.map((facet) => [
    ...getFilterSelectedItems(querystring, facet),
  ]);

  let facetValues: Facet[] = [];
  facetIdsArray?.forEach((ids, index) => {
    ids.forEach((id) => {
      // Flatten the facet values to include all children for filtering, otherwise we would only be able to filter
      // by the top-level facet values.
      const facetFlattenedValues = flattenForChildren(facets[index].values);
      const filteredValues = facetFlattenedValues.filter(
        (value) => value.filter === id,
      );

      facetValues.push({ ...facets[index], values: filteredValues });
    });
  });

  return facetValues;
};

export interface UseSearchParams {
  searchUrl: string;
  cleanFacetsNumber?: number;
  cleanFacetsOrder?: string[];
}

/**
 * Main hook that is used to encapsulate logic about search.
 */
export const useSearch = ({
  searchUrl,
  cleanFacetsNumber = 2,
  cleanFacetsOrder = [],
}: UseSearchParams): UseSearchType => {
  return {
    getFilterSelectedItems,
    toggleFilter: (filter: string, selectedItems: string[], alias?: string) =>
      toggleFilter(
        searchUrl,
        filter,
        cleanFacetsNumber,
        cleanFacetsOrder,
        selectedItems,
        alias,
      ),
    redirectWithoutFulltextAndLocation: () =>
      redirectWithoutFulltextAndLocation(searchUrl),
    redirectWithFulltextAndOrLocation: (
      fulltext?: string,
      location?: GeoInput,
    ) => redirectWithFulltextAndOrLocation(searchUrl, fulltext, location),
    getFilterSelectedFacets: (querystring: string, facets: Facet[]): Facet[] =>
      extractSelectedFacetValues(querystring, facets),
  };
};
